import React, { ComponentType, FC, Suspense, useEffect, useState } from 'react';
import { Result } from 'antd';
import { Redirect, Route } from 'react-router-dom';
import Loader from 'components/Loader';
import Layout from 'components/Layout';
import CheckMaintenance from 'components/Maintenance/CheckMaintenance';
import { UserResponse } from 'types';
import { useDispatch } from 'react-redux';
import { getChecklists, getRequestForms } from 'utils/jsonCache';
import { setChecklists, setRequestForms } from 'store/container/resources/resourcesSlice';
import { getProjectSelect } from '../../modules/project/store/projectSelect-slice';

interface AuthenticationRouteProps {
  auth?: {
    user: UserResponse | null;
    loading: boolean;
  };
  component: ComponentType<any>;
  path: string;
  type?: 'regular_user' | 'contact' | 'freelancer';
  isAuth?: boolean | null;
  allowInMaintenance?: boolean;
  noLayout?: boolean;
  exact?: boolean;
  overflowScroll?: boolean;
}

const AuthenticationRoute: FC<AuthenticationRouteProps> = ({
  auth,
  isAuth = null,
  allowInMaintenance = false,
  noLayout = false,
  component: RouteComponent,
  path,
  type,
  exact,
  overflowScroll,
}) => {
  const { user, loading } = auth ?? {};

  const [isPreloadComplete, setIsPreloadComplete] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (user) {
      dispatch(getProjectSelect());
    }

    if (user && window.clarity) {
      window.clarity('identify', user.email);
    }
  }, [dispatch, user]);

  useEffect(() => {
    async function preload() {
      try {
        const [checklists, requestForms] = await Promise.all([getChecklists(), getRequestForms()]);

        dispatch(setChecklists(checklists));
        dispatch(setRequestForms(requestForms));
      } catch (error) {
        console.error('Error preloading resources:', error);
      } finally {
        setIsPreloadComplete(true);
      }
    }

    if (user) {
      preload();
    }
  }, [dispatch, user]);

  return (
    <Route
      path={path}
      exact={exact}
      render={(props) => {
        const appIsLoading = loading || (user && !isPreloadComplete);

        if (appIsLoading && (isAuth !== null || !noLayout)) {
          return <Loader />;
        }

        if (loading && !user && (isAuth !== null || !noLayout)) {
          return <Loader />;
        }

        if (user && isAuth === false) {
          const redirect = props.location.state as any;

          return <Redirect to={redirect?.from ?? '/'} />;
        }

        if (!user && isAuth === true) {
          return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />;
        }

        if (user && type && user.contact.type !== type) {
          return (
            <CheckMaintenance user={user} allowInMaintenance={allowInMaintenance}>
              <Layout overflowScroll={overflowScroll}>
                <Result status="warning" title="Sorry, you don't have permission to access this page" />
              </Layout>
            </CheckMaintenance>
          );
        }

        if (!user || noLayout) {
          return (
            <CheckMaintenance user={user} allowInMaintenance={allowInMaintenance}>
              <RouteComponent user={user} />
            </CheckMaintenance>
          );
        }

        return (
          <CheckMaintenance user={user} allowInMaintenance={allowInMaintenance}>
            <Layout overflowScroll={overflowScroll}>
              <Suspense fallback={<Loader />}>
                <RouteComponent user={user} />
              </Suspense>
            </Layout>
          </CheckMaintenance>
        );
      }}
    />
  );
};

export default AuthenticationRoute;
